import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Zap, Star, ArrowRight, Home, RefreshCw, Users, X, ClipboardCheck } from 'lucide-react';
import { type ModuleData, type ModuleId, getFamilyDefinition } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG } from '../../config/constants';
import { useDjaySelection, getDjayGreeting, DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { DjayRecommendationCard } from './DjayRecommendationCard';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants, containerVariants } from '../shared/animations';
import { AssignedAssessmentBanner } from './AssignedAssessmentBanner';
import { PracticeAssignmentBanner } from './PracticeAssignmentBanner';
import { dataLayer } from '../../data/dataLayer';
import type { AssessmentConfig, TeacherAssignment, PracticeAssignment } from '../../assessment';
import { DOMAIN_MODULE_MAP } from '../../assessment';
import { soundManager } from '../../utils/soundManager';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import { hasActiveDecay, getWeakestFamiliesGlobal, getDjayRecommendation } from '../../mastery';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { DailyChallengeCard } from './DailyChallengeCard';
import { GlanceBar } from './GlanceBar';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
  onNavigateToAssessment: () => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onNavigateToLab?: () => void;
  onStartMode?: (mode: string, config?: any) => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onNavigateToAssessment,
  onStartAssessment,
  onNavigateToLab,
  onStartMode
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts,
    activeAccount
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const [activeAssignments, setActiveAssignments] = useState<TeacherAssignment[]>([]);
  const [activePracticeAssignments, setActivePracticeAssignments] = useState<PracticeAssignment[]>([]);
  const [showKlascodeBanner, setShowKlascodeBanner] = useState(false);
  // Check if learner has no classroom and banner not dismissed
  useEffect(() => {
    let cancelled = false;
    async function checkKlascodeBanner() {
      if (!activeAccount || activeAccount.role !== 'learner') {
        if (!cancelled) setShowKlascodeBanner(false);
        return;
      }
      // Check if banner was dismissed
      const dismissed = await dataLayer.getKlascodeBannerDismissed();
      if (cancelled) return;
      if (dismissed) {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if learner is in any classroom
      const hasClassroom = accounts && Object.values(accounts.classrooms || {}).some(classroom => classroom.learnerIds.includes(activeAccount.id));
      if (!cancelled) setShowKlascodeBanner(!hasClassroom);
    }
    checkKlascodeBanner();
    return () => {
      cancelled = true;
    };
  }, [activeAccount, accounts]);
  const handleDismissKlascodeBanner = async () => {
    await dataLayer.setKlascodeBannerDismissed(true);
    setShowKlascodeBanner(false);
  };
  useEffect(() => {
    let cancelled = false;
    async function loadAssignments() {
      if (!activeAccount) return;
      const [allAssessments, allPractice] = await Promise.all([dataLayer.getTeacherAssignments(), dataLayer.getPracticeAssignments()]);
      if (cancelled) return;
      const now = new Date();
      const validAssessments = Object.values(allAssessments).filter(assignment => {
        if (assignment.group !== selectedGroup) return false;
        if (assignment.deadline && new Date(assignment.deadline) < now) return false;
        const completedBy = assignment.completedByStudentIds || [];
        if (completedBy.includes(activeAccount.id)) return false;
        if (assignment.wholeClass) return true;
        if (assignment.targetStudentIds && assignment.targetStudentIds.includes(activeAccount.id)) return true;
        return false;
      });
      const validPractice = Object.values(allPractice).filter(assignment => {
        if (assignment.deadline && new Date(assignment.deadline) < now) return false;
        const completedBy = assignment.completedByStudentIds || [];
        if (completedBy.includes(activeAccount.id)) return false;
        if (assignment.wholeClass) return true;
        if (assignment.targetStudentIds && assignment.targetStudentIds.includes(activeAccount.id)) return true;
        return false;
      });
      if (!cancelled) {
        setActiveAssignments(validAssessments);
        setActivePracticeAssignments(validPractice);
      }
    }
    loadAssignments();
    return () => {
      cancelled = true;
    };
  }, [activeAccount, selectedGroup]);
  const hour = new Date().getHours();
  let greeting = 'Hallo';
  if (hour < 8) greeting = 'Goedemorgen';else if (hour < 12) greeting = 'Goedemorgen';else if (hour < 18) greeting = 'Goedemiddag';else greeting = 'Goedenavond';
  const djayEmotion: DjayEmotion = useMemo(() => getDjayGreeting(displayName).emotion,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [hour]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  const weeklyGoal = state.dailyStreak?.weeklyGoalTarget ?? 3;
  const level = state.stats.level;
  const xp = state.stats.xp;
  // Dynamic subtitle based on daily progress
  const dynamicSubtitle = useMemo(() => {
    const remaining = dailyGoal - todayQuestionsAnswered;
    if (todayQuestionsAnswered >= dailyGoal) {
      return 'Je dagdoel is gehaald! 🎉';
    } else if (todayQuestionsAnswered > 0) {
      return `Nog ${remaining} ${remaining === 1 ? 'vraag' : 'vragen'} voor je dagdoel!`;
    } else {
      return `Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`;
    }
  }, [todayQuestionsAnswered, dailyGoal, selectedGroup]);
  const recommendation = useMemo(() => {
    return getDjayRecommendation(state.familyMastery || {}, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions);
  }, [state.familyMastery, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions]);
  const recommendedModule = useMemo(() => {
    return recommendation ? modules.find(m => m.id === recommendation.moduleId) : null;
  }, [recommendation, modules]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  // Find last practiced module
  const lastPracticedModule = useMemo(() => {
    if (!state.questionStates) return null;
    let latestTime = 0;
    let latestModuleId: ModuleId | null = null;
    // We can iterate over modules and find the max lastAttemptAt
    modules.forEach(m => {
      const answered = getAnsweredQuestions(m.id);
      answered.forEach(q => {
        if (q.lastAttemptAt && q.lastAttemptAt > latestTime) {
          latestTime = q.lastAttemptAt;
          latestModuleId = m.id;
        }
      });
    });
    return latestModuleId ? modules.find(m => m.id === latestModuleId) : null;
  }, [modules, getAnsweredQuestions, state.questionStates]);
  // Get decaying families
  const decayingFamilies = useMemo(() => {
    if (!state.familyMastery) return [];
    const weakest = getWeakestFamiliesGlobal(state.familyMastery, selectedGroup, 10);
    return weakest.filter(f => hasActiveDecay(state.familyMastery?.[f.key])).slice(0, 2);
  }, [state.familyMastery, selectedGroup]);
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Daily Challenge
  const dailyChallenge = useMemo(() => {
    return getTodayChallenge(state, modules, selectedGroup);
  }, [state, modules, selectedGroup]);
  const handleStartChallenge = () => {
    if (dailyChallenge.type === 'herhaal' && dailyChallenge.decayingFamilyId && onStartMode) {
      onStartMode('sprint', {
        targetFamilyId: dailyChallenge.decayingFamilyId
      });
    } else if (dailyChallenge.moduleId) {
      onStartPractice(dailyChallenge.moduleId);
    } else {
      onNavigateToModules();
    }
  };
  return <PageContainer data-id="element-1395">
      <GlanceBar streakDays={currentStreak} dailyProgress={todayQuestionsAnswered} dailyGoalTarget={dailyGoal} level={level} xp={xp} weeklyGoal={weeklyGoal} weeklyActivity={weeklyActivity} onNavigateToModules={onNavigateToModules} data-id="element-1396" />

      <motion.div variants={itemVariants} className="mb-6 sm:mb-8" data-id="element-1397">
        <PageHero title={`${greeting}, ${displayName}!`} subtitle={dynamicSubtitle} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={djayEmotion} djayVariant={selectedVariant} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={['🚀', '⭐']} data-id="element-1398" />
      </motion.div>

      {/* KLASCODE BANNER */}
      {showKlascodeBanner && <motion.div variants={itemVariants} data-id="element-1399">
          <Card className="p-3 sm:p-4 border-2 border-amber-200 bg-amber-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden shadow-sm" data-id="element-1400">
            <div className="flex items-center gap-3" data-id="element-1401">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 shadow-sm" data-id="element-1402">
                <Users className="w-5 h-5" data-id="element-1403" />
              </div>
              <div data-id="element-1404">
                <h3 className="text-sm font-bold text-amber-900" data-id="element-1405">
                  Heb je een klascode?
                </h3>
                <p className="text-xs text-amber-700 font-medium" data-id="element-1406">
                  Je kunt deze op elk moment invullen via Instellingen.
                </p>
              </div>
            </div>
            <button onClick={handleDismissKlascodeBanner} className="absolute top-2 right-2 sm:static p-1.5 text-amber-500 hover:bg-amber-100 rounded-lg transition-colors" title="Sluiten" data-id="element-1407">
              <X className="w-4 h-4" data-id="element-1408" />
            </button>
          </Card>
        </motion.div>}

      {/* ACTIVE ASSIGNMENTS */}
      {activeAssignments.length > 0 && <motion.div variants={itemVariants} data-id="element-1409">
          <Card className="p-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-1410">
            <div className="flex items-center gap-3" data-id="element-1411">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0 shadow-sm" data-id="element-1412">
                <ClipboardCheck className="w-5 h-5" data-id="element-1413" />
              </div>
              <div data-id="element-1414">
                <h3 className="text-sm font-bold text-indigo-900" data-id="element-1415">
                  {activeAssignments.length}{' '}
                  {activeAssignments.length === 1 ? 'toets staat' : 'toetsen staan'}{' '}
                  voor je klaar!
                </h3>
                <p className="text-xs text-indigo-700 font-medium" data-id="element-1416">
                  Je leraar heeft nieuwe toetsen klaargezet.
                </p>
              </div>
            </div>
            <Button onClick={onNavigateToAssessment} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1417">
              Bekijk toetsen <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1418" />
            </Button>
          </Card>
        </motion.div>}

      {/* ACTIVE PRACTICE ASSIGNMENTS */}
      {activePracticeAssignments.length > 0 && <motion.div variants={itemVariants} data-id="element-1419">
          {activePracticeAssignments.length === 1 ? <PracticeAssignmentBanner assignment={activePracticeAssignments[0]} teacherName="je leraar" onStartPractice={() => {
        if (activePracticeAssignments[0].moduleIds.length > 0) {
          onStartPractice(activePracticeAssignments[0].moduleIds[0]);
        } else {
          onNavigateToModules();
        }
      }} data-id="element-1420" /> : <Card className="p-4 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-1421">
              <div className="flex items-center gap-3" data-id="element-1422">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0 shadow-sm" data-id="element-1423">
                  <ClipboardCheck className="w-5 h-5" data-id="element-1424" />
                </div>
                <div data-id="element-1425">
                  <h3 className="text-sm font-bold text-purple-900" data-id="element-1426">
                    {activePracticeAssignments.length} oefenopdrachten staan
                    klaar!
                  </h3>
                  <p className="text-xs text-purple-700 font-medium" data-id="element-1427">
                    Je leraar heeft nieuwe oefeningen voor je klaargezet.
                  </p>
                </div>
              </div>
              <Button onClick={onNavigateToModules} size="sm" className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white shadow-sm" data-id="element-1428">
                Bekijk opdrachten <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1429" />
              </Button>
            </Card>}
        </motion.div>}

      {/* QUICK NAVIGATION */}
      <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3 sm:gap-4" data-id="element-1430">
        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToModules();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50 transition-all group shadow-sm" data-id="element-1431">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1432">
            <Target className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1433" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1434">
            Mijn Werelden
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToLab();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-purple-200 hover:bg-purple-50 transition-all group shadow-sm" data-id="element-1435">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1436">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1437" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1438">
            Oefenlab
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToAssessment();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-orange-200 hover:bg-orange-50 transition-all group shadow-sm" data-id="element-1439">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1440">
            <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1441" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1442">
            Toets
          </span>
        </button>
      </motion.div>

      {/* PRIMARY ACTION */}
      <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1443">
        {hasStarted && lastPracticedModule ? <ContinueCard module={lastPracticedModule} onStart={() => onStartPractice(lastPracticedModule.id)} djayVariant={selectedVariant} data-id="element-1444" /> : hasStarted && recommendedModule && recommendation ? <DjayRecommendationCard module={recommendedModule} recommendation={recommendation} onStart={() => onStartPractice(recommendedModule.id)} djayVariant={selectedVariant} data-id="element-1445" /> : !hasStarted ? <WelcomeCard onStart={onNavigateToModules} djayVariant={selectedVariant} data-id="element-1446" /> : null}
      </motion.div>

      {/* DAILY CHALLENGE */}
      {hasStarted && <motion.div variants={itemVariants} data-id="element-1447">
          <DailyChallengeCard challenge={dailyChallenge} djayVariant={selectedVariant} onStart={handleStartChallenge} data-id="element-1448" />
        </motion.div>}

      {/* HERHAAL TIP */}
      {decayingFamilies.length > 0 && dailyChallenge.type !== 'herhaal' && <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1449">
          {decayingFamilies.map(f => {
        const def = getFamilyDefinition(f.familyId);
        const title = def?.description || f.familyId;
        const module = modules.find(m => f.familyId.includes(m.id));
        return <Card key={f.familyId} className="p-4 sm:p-5 border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl cursor-pointer hover:shadow-lg transition-all group shadow-md" onClick={() => module && onStartPractice(module.id)} data-id="element-1450">
                <div className="flex items-center gap-3 sm:gap-4" data-id="element-1451">
                  <div className="flex-shrink-0" data-id="element-1452">
                    <DjayAvatar size="xs" emotion="thinking" variant={selectedVariant} animate data-id="element-1453" />
                  </div>
                  <div className="flex-1" data-id="element-1454">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1" data-id="element-1455">
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600" data-id="element-1456" />
                      <h3 className="text-sm sm:text-base font-black text-teal-900" data-id="element-1457">
                        Tijd voor een opfrisser!
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-teal-700 font-medium leading-relaxed" data-id="element-1458">
                      Je hebt <strong className="text-teal-900" data-id="element-1459">{title}</strong>{' '}
                      al een tijdje niet geoefend.
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 group-hover:bg-teal-500 group-hover:text-white transition-colors flex-shrink-0 shadow-sm" data-id="element-1460">
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1461" />
                  </div>
                </div>
              </Card>;
      })}
        </motion.div>}
    </PageContainer>;
}
function WelcomeCard({
  onStart,
  djayVariant
}: {
  onStart: () => void;
  djayVariant: DjayVariant;
}) {
  return <Card className="p-6 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-lg relative overflow-hidden rounded-[2rem]" data-id="element-1462">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1463" />
      <div className="relative z-10 flex flex-col items-center" data-id="element-1464">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-4 sm:mb-6" data-id="element-1465">
          <div className="block sm:hidden" data-id="element-1466">
            <DjayAvatar size="md" emotion="celebrating" variant={djayVariant} animate data-id="element-1467" />
          </div>
          <div className="hidden sm:block" data-id="element-1468">
            <DjayAvatar size="lg" emotion="celebrating" variant={djayVariant} animate data-id="element-1469" />
          </div>
        </motion.div>
        <h2 className="text-xl sm:text-3xl font-black text-slate-800 mb-2 sm:mb-3 tracking-tight" data-id="element-1470">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="hidden sm:block text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1471">
          Je avontuur begint hier. Kies een wereld om te oefenen, verdien XP en
          bouw je streak op! 🚀
        </p>
        <Button onClick={onStart} variant="primary" size="lg" className="shadow-lg rounded-2xl sm:h-14 sm:text-lg sm:px-8 hover:shadow-xl transition-shadow" data-id="element-1472">
          <Star className="w-5 h-5 sm:w-6 sm:h-6 mr-2 fill-current" data-id="element-1473" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}
function ContinueCard({
  module,
  onStart,
  djayVariant
}: {
  module: ModuleData;
  onStart: () => void;
  djayVariant: DjayVariant;
}) {
  const colorMap: Record<string, {
    bgGradient: string;
    border: string;
    textColor: string;
    btnBg: string;
    btnHover: string;
  }> = {
    'text-blue-500': {
      bgGradient: 'from-blue-50 to-indigo-50',
      border: 'border-blue-200',
      textColor: 'text-blue-900',
      btnBg: 'bg-blue-600',
      btnHover: 'hover:bg-blue-700'
    },
    'text-indigo-500': {
      bgGradient: 'from-indigo-50 to-violet-50',
      border: 'border-indigo-200',
      textColor: 'text-indigo-900',
      btnBg: 'bg-indigo-600',
      btnHover: 'hover:bg-indigo-700'
    },
    'text-orange-500': {
      bgGradient: 'from-orange-50 to-amber-50',
      border: 'border-orange-200',
      textColor: 'text-orange-900',
      btnBg: 'bg-orange-600',
      btnHover: 'hover:bg-orange-700'
    },
    'text-sky-500': {
      bgGradient: 'from-sky-50 to-blue-50',
      border: 'border-sky-200',
      textColor: 'text-sky-900',
      btnBg: 'bg-sky-600',
      btnHover: 'hover:bg-sky-700'
    },
    'text-green-500': {
      bgGradient: 'from-green-50 to-emerald-50',
      border: 'border-green-200',
      textColor: 'text-green-900',
      btnBg: 'bg-green-600',
      btnHover: 'hover:bg-green-700'
    },
    'text-rose-500': {
      bgGradient: 'from-rose-50 to-pink-50',
      border: 'border-rose-200',
      textColor: 'text-rose-900',
      btnBg: 'bg-rose-600',
      btnHover: 'hover:bg-rose-700'
    },
    'text-amber-500': {
      bgGradient: 'from-amber-50 to-yellow-50',
      border: 'border-amber-200',
      textColor: 'text-amber-900',
      btnBg: 'bg-amber-600',
      btnHover: 'hover:bg-amber-700'
    },
    'text-purple-500': {
      bgGradient: 'from-purple-50 to-fuchsia-50',
      border: 'border-purple-200',
      textColor: 'text-purple-900',
      btnBg: 'bg-purple-600',
      btnHover: 'hover:bg-purple-700'
    },
    'text-teal-500': {
      bgGradient: 'from-teal-50 to-emerald-50',
      border: 'border-teal-200',
      textColor: 'text-teal-900',
      btnBg: 'bg-teal-600',
      btnHover: 'hover:bg-teal-700'
    },
    'text-cyan-500': {
      bgGradient: 'from-cyan-50 to-sky-50',
      border: 'border-cyan-200',
      textColor: 'text-cyan-900',
      btnBg: 'bg-cyan-600',
      btnHover: 'hover:bg-cyan-700'
    }
  };
  const theme = colorMap[module.color] || colorMap['text-blue-500'];
  const {
    bgGradient,
    border,
    textColor,
    btnBg,
    btnHover
  } = theme;
  return <Card className={`p-4 sm:p-6 border-2 ${border} bg-gradient-to-r ${bgGradient} rounded-2xl shadow-md relative overflow-hidden group cursor-pointer`} onClick={onStart} data-id="element-1474">
      <div className="absolute right-0 top-0 w-32 h-32 bg-white/40 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-1475" />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10" data-id="element-1476">
        <div className="flex items-center gap-4" data-id="element-1477">
          <div className="flex-shrink-0" data-id="element-1478">
            <DjayAvatar size="sm" emotion="happy" variant={djayVariant} animate data-id="element-1479" />
          </div>
          <div data-id="element-1480">
            <h3 className={`text-xs font-bold ${textColor} uppercase tracking-wider mb-1 opacity-80`} data-id="element-1481">
              Ga verder waar je gebleven was
            </h3>
            <p className={`text-xl font-black ${textColor}`} data-id="element-1482">{module.title}</p>
            <div className="flex items-center gap-2 mt-2" data-id="element-1483">
              <div className="h-1.5 w-24 bg-white/60 rounded-full overflow-hidden" data-id="element-1484">
                <div className={`h-full ${btnBg} rounded-full w-2/3`} data-id="element-1485" />
              </div>
              <span className={`text-xs font-bold ${textColor} opacity-70`} data-id="element-1486">
                Lekker bezig!
              </span>
            </div>
          </div>
        </div>
        <Button onClick={e => {
        e.stopPropagation();
        onStart();
      }} size="lg" className={`${btnBg} ${btnHover} text-white shadow-sm rounded-xl w-full sm:w-auto transition-transform group-hover:scale-105`} data-id="element-1487">
          Ga verder <ArrowRight className="w-5 h-5 ml-2" data-id="element-1488" />
        </Button>
      </div>
    </Card>;
}